/** «Данные с вейверов» сохраняются сами, построчно (03.10.2026, Влад: «добавил человека в Юнифанс,
 *  у него сессии нету, как тайминги получить»). Раньше табличка уходила на сервер только со сдачей
 *  отчёта: вписанный номер с чипом «✈ в Юнифансе» ни к чему не привязывался. Теперь каждая строка
 *  пишется через ~0,8 с после правки (POST /waiver-persons/row, удаление — DELETE …/row/:id), и
 *  сервер сразу привязывает человека к игре по телефону — игра и тайминги появляются у него.
 *
 *  Построчно по id строки, а не всей табличкой: в неё же пишут напарник и бот вейверов, и запись
 *  целиком из своей копии возвращала чужие удаления и стирала чужие строки (ревью 03.10).
 *  Строку узнаём по локальному lid (стабилен между правками), серверный id держим отдельно —
 *  устаревший объект строки из формы не потеряет id и не создаст дубль. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { ApiException } from "../../api/types";
import type { WPerson } from "./reportPlayers";

export interface WaiverSaveState { kind: "idle" | "saving" | "saved" | "error"; msg?: string }

const digits = (v: string | null | undefined) => String(v ?? "").replace(/\D/g, "");
/** Строка, которую стоит хранить на сервере: есть ФИО или номер целиком. */
export const meaningful = (p: WPerson) => !!p.fio.trim() || digits(p.phone).length >= 10;
const body = (p: WPerson) => ({ fio: p.fio.trim(), birth_date: p.birth_date || undefined, phone: p.phone || undefined, telegram: p.telegram || undefined });
const sig = (p: WPerson | undefined) => (p && meaningful(p) ? JSON.stringify(body(p)) : "");
let seq = 0;
/** lid уходит на сервер как client_key: повтор вставки после потерянного ответа обновит ту же строку (03.10). */
const newLid = () => `w${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}${(seq++).toString(36)}`;

export function useWaiverSync(bookingId: number) {
  const [persons, setPersons] = useState<WPerson[]>([]);
  const [state, setState] = useState<WaiverSaveState>({ kind: "idle" });
  const latest = useRef<WPerson[]>([]);
  const ids = useRef(new Map<string, number>());      // lid → id строки на сервере
  const saved = useRef(new Map<string, string>());    // lid → что лежит на сервере (подпись)
  const pending = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const fails = useRef(0);
  const alive = useRef(true);

  const apply = (next: WPerson[]) => { latest.current = next; setPersons(next); };

  /** Загрузка с сервера: серверные строки — после своих несохранённых (индексы открытых полей не сдвигаются). */
  const seed = useCallback((srv: WPerson[]) => {
    const have = new Set([...ids.current.values()]);
    const add = srv.filter((p) => !p.id || !have.has(p.id)).map((p) => {
      const lid = newLid();
      if (p.id) { ids.current.set(lid, p.id); saved.current.set(lid, sig(p)); }
      return { ...p, lid };
    });
    apply([...latest.current, ...add]);
  }, []);

  /** Привести одну строку на сервере к тому, что в форме. */
  const reconcile = async (lid: string) => {
    const row = latest.current.find((p) => p.lid === lid);
    const id = ids.current.get(lid);
    if (!row || !meaningful(row)) {   // строку убрали или очистили
      if (id) await api(`/api/game-reports/waiver-persons/row/${id}?booking_id=${bookingId}`, { method: "DELETE", retries: 0 });
      ids.current.delete(lid); saved.current.delete(lid);
      return;
    }
    const s = sig(row);
    if (id && saved.current.get(lid) === s) return;
    const r = await api<{ ok?: boolean; id?: number }>("/api/game-reports/waiver-persons/row", { method: "POST", retries: 0, body: { booking_id: bookingId, id: id ?? null, client_key: lid, ...body(row) } });
    if (r.id) ids.current.set(lid, r.id);
    saved.current.set(lid, s);
  };

  const run = useCallback((): Promise<void> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    chain.current = chain.current.then(async () => {
      if (!pending.current.size) return;
      setState({ kind: "saving" });
      const lids = [...pending.current];
      pending.current.clear();
      let err: unknown = null;
      for (const lid of lids) {
        try { await reconcile(lid); } catch (e) { err = err ?? e; pending.current.add(lid); }
      }
      if (!err) { fails.current = 0; setState(pending.current.size ? { kind: "saving" } : { kind: "saved" }); return; }
      const status = err instanceof ApiException ? err.status : 0;
      const msg = err instanceof ApiException ? String((err.body as { error?: string } | null)?.error ?? "") : "";
      /* 4xx и 503 (ключ шифрования) — повтор не поможет, показываем причину; связь — повторим сами до трёх раз */
      const hopeless = (status >= 400 && status < 500 && status !== 408 && status !== 429) || status === 503;
      setState({ kind: "error", msg: hopeless ? msg || "сервер отказал" : undefined });
      if (!hopeless && alive.current && ++fails.current <= 3 && !timer.current) timer.current = setTimeout(() => { timer.current = null; void run(); }, 4000);
    });
    return chain.current;
  }, [bookingId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Правка из формы: какие строки изменились — те и запишем через ~0,8 с. */
  const edit = useCallback((next: WPerson[]) => {
    const withLid = next.map((p) => (p.lid ? p : { ...p, lid: newLid() }));
    const prev = new Map(latest.current.map((p) => [p.lid ?? "", p]));
    const nowLids = new Set(withLid.map((p) => p.lid as string));
    for (const p of withLid) {
      const lid = p.lid as string;
      if (sig(p) !== sig(prev.get(lid)) || (!meaningful(p) && ids.current.has(lid))) pending.current.add(lid);
    }
    /* убранную строку ставим в очередь всегда: её первая вставка может быть ещё в пути — id придёт, и следом уйдёт удаление */
    for (const [lid, p] of prev) if (lid && !nowLids.has(lid) && (ids.current.has(lid) || meaningful(p))) pending.current.add(lid);
    apply(withLid);
    if (!pending.current.size) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void run(); }, 800);
  }, [run]);

  /** Отправка отчёта: дописать то, что ещё не ушло (табличка целиком больше не пишется). */
  const flush = useCallback(() => run(), [run]);

  /* закрыли форму с несохранённой правкой — дописать в фоне */
  useEffect(() => { alive.current = true; return () => { alive.current = false; if (pending.current.size) void run(); }; }, [run]);

  return { persons, state, seed, edit, flush };
}
